/**
 * Generic FAQ accordion section.
 * - `title` : heading rendered inside the section.
 * - `faqs` : array of { q, a } objects.
 * - `renderAnswer` (optional) : custom renderer for the answer (e.g. markdown-lite).
 *   Falls back to plain text rendering.
 * - `testIdPrefix` (optional) : when provided, each item receives
 *   `data-testid="{prefix}-{index}"`.
 * - `bgClassName` (optional) : background class for the outer section.
 */
export default function FaqAccordion({
    title,
    faqs,
    renderAnswer,
    testIdPrefix,
    bgClassName = "bg-[var(--paper)] border-y border-[var(--line)]",
    intro,
}) {
    return (
        <section className={bgClassName}>
            <div className="max-w-4xl mx-auto px-5 lg:px-10 py-14">
                <div className="divider-amber mb-5" />
                <h2 className="heading text-3xl lg:text-4xl font-extrabold">{title}</h2>
                {intro && <p className="mt-3 text-[var(--ink-soft)]">{intro}</p>}
                <div className="mt-8 divide-y divide-[var(--line)] border-y border-[var(--line)] bg-white rounded-2xl">
                    {faqs.map((f, i) => (
                        <details
                            key={f.q}
                            className="group p-5"
                            data-testid={testIdPrefix ? `${testIdPrefix}-${i}` : undefined}
                        >
                            <summary className="flex items-start justify-between cursor-pointer list-none gap-4">
                                <span className="heading font-bold text-[var(--navy)] pr-4">{f.q}</span>
                                <i className="fa-solid fa-plus text-[var(--amber)] mt-1 shrink-0 group-open:rotate-45 transition-transform" />
                            </summary>
                            <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">
                                {renderAnswer ? renderAnswer(f.a) : f.a}
                            </p>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    );
}
